/**
 * A person's own sign-ins to their organization's connectors (item 65A) —
 * the client side of /me/connectors. For the signed-in person only; people
 * never add a connector, they connect to the ones their organization chose.
 *
 * Connecting with an account opens the service's sign-in in a popup. Its
 * answer comes back to THIS window only (utils/oauthPopup.ts): the landing
 * page hands it over, and this window finishes the sign-in as its own person
 * — a sign-in link opened anywhere else finishes nothing.
 */

import api from '@services/api';
import { openOAuthPopup, runOAuthPopup } from '@/utils/oauthPopup';

export type MyConnectorStatus = 'connected' | 'not_connected' | 'paused' | 'expired' | 'removed';

export interface MyConnector {
  entry_id: string;
  name: string;
  kind: 'mcp' | 'api';
  slug: string;
  description: string;
  signin_mode: 'per_person_oauth' | 'per_person_key';
  url: string | null;
  status: MyConnectorStatus;
  /** "Connected as …" when the service or the person named the account. */
  account_label: string | null;
  /** Why it is paused: the organization does not get it now, or it was deleted. */
  paused_reason: 'not_available' | 'deleted' | null;
  removed_reason: 'paused_90_days' | 'deactivated' | null;
  connected_at: string | null;
  /** A per-person key: where it goes — never a value. */
  key_shape?: { header_name?: string | null; auth_type?: string; auth_meta?: { in?: string; name?: string } };
}

export interface KeyCredential { value?: string; username?: string; password?: string; label?: string }

const ME = '/me/connectors';
const POPUP_LIMIT_MS = 10 * 60 * 1000;     // the server's state lives 10 minutes

export type ConnectOutcome = 'connected' | 'declined' | 'not_available' | 'invalid_state' | 'error'
  | 'cancelled' | 'blocked';

export interface ConnectWait {
  outcome: Promise<ConnectOutcome>;
  /** Stop waiting (the person closed the window, or chose otherwise). */
  cancel: () => void;
}

class MyConnectorsService {
  async list(scope?: { chatbotId: string; conversationId?: string | null }): Promise<MyConnector[]> {
    const params = scope ? { chatbot_id: scope.chatbotId, ...(scope.conversationId ? { conversation_id: scope.conversationId } : {}) } : {};
    const { data } = await api.get(ME, { params });
    return data.connectors || [];
  }

  async get(entryId: string): Promise<MyConnector> {
    const { data } = await api.get(`${ME}/${entryId}`);
    return data.connector;
  }

  async saveKey(entryId: string, credential: KeyCredential): Promise<MyConnector> {
    const { data } = await api.put(`${ME}/${entryId}/key`, { credential });
    return data.connector;
  }

  async disconnect(entryId: string): Promise<void> {
    await api.delete(`${ME}/${entryId}`);
  }

  /** "Not now" to a chat's waiting connect prompt. */
  async notNow(entryId: string): Promise<void> {
    await api.post(`${ME}/${entryId}/not-now`, {});
  }

  async dismiss(entryId: string): Promise<void> {
    await api.post(`${ME}/${entryId}/dismiss`, {});
  }

  /**
   * Sign in with an account. MUST be called inside the click: the popup
   * opens at once (later, the browser blocks it), then goes to the service.
   * The outcome is the server's, once this window has finished the sign-in
   * with the answer the landing page handed back; 'cancelled' when the wait
   * was stopped or ran out, 'blocked' when no popup could open.
   */
  connectWithAccount(entryId: string): ConnectWait {
    const popup = openOAuthPopup(`connect-${entryId}`);
    if (!popup) return { outcome: Promise.resolve('blocked'), cancel: () => {} };
    const run = runOAuthPopup(popup, {
      start: async () => (await api.post(`${ME}/${entryId}/connect`, {})).data,
      finish: async (callback) => {
        const { data } = await api.post(`${ME}/oauth/finish`, callback);
        return { outcome: data.outcome, detail: { name: data.entry_name ?? '' } };
      },
      limitMs: POPUP_LIMIT_MS,
    });
    // Out of time is the same as stopped here: nothing changed, nothing to say.
    const outcome = run.outcome.then((o) => (o === 'timeout' ? 'cancelled' : o) as ConnectOutcome);
    return { outcome, cancel: run.cancel };
  }

}

export const myConnectorsService = new MyConnectorsService();
